// ═══════════════════════════════════════════════════════════════════════
// HOMEPAGE ("/") — VERBATIM COPY from 7figureclosingclub.com
// Rebuilt with the 7FCC blue/silver system. Copy is word-for-word from
// the live page; only widget internals flagged as PENDING were not
// retrievable from the live DOM (see component comments).
// ═══════════════════════════════════════════════════════════════════════

export const APPLY_URL = "/apply";

export const HOME = {
  brand: {
    eyebrow: "Elite High-Ticket Sales Training",
  },
  hero: {
    headlineLines: [
      "Become The Setter Or Closer",
      "High-Ticket Teams Can Actually",
      "Trust With Leads",
    ],
    sub: "7 Figure Closing Club trains serious students in the call structure, objection handling, follow-up discipline, and performance standards required to earn a seat in high-ticket sales.",
    ctaPrimary: { label: "Take The Sales Readiness Assessment", anchor: "assessment" },
    ctaSecondary: { label: "View The 7FCC Method", anchor: "method" },
  },
  band: {
    line1: "Real standards. Real reps. Real correction. Real execution. No random scripts. No hiding behind motivation.",
    line2: "Just reps, review, correction, and execution.",
    chips: [
      "Setter Certification",
      "Closer Development",
      "Live Drills",
      "Fireflies Call Review",
      "CRM Discipline",
      "Objection Training",
    ],
  },
  problem: {
    eyebrow: "The Real Problem",
    headline:
      "Most People Don't Fail Sales Because They're Lazy. They Fail Because They Were Never Trained Properly.",
    items: [
      {
        num: "01",
        title: "They Copy Scripts But Never Learn Control",
        body: "A memorized line collapses the second the call goes off-script. Control comes from understanding the frame — not reciting words.",
      },
      {
        num: "02",
        title: "They Make Calls Without Tracking Numbers",
        body: "If you don't measure dials, contacts, and conversion, you're guessing. You can't fix what you refuse to count.",
      },
      {
        num: "03",
        title: "They Get Objections And Immediately Collapse",
        body: 'The first "no" ends the call because they were never drilled on what to say next. Objections are a skill, not luck.',
      },
      {
        num: "04",
        title: "They Never Review Their Recordings",
        body: "Without listening back, every mistake repeats forever. Review is where amateurs quietly become professionals.",
      },
    ],
  },
  enemy: {
    eyebrow: "The Enemy",
    headline: "The Real Enemy Is Random Sales Advice.",
    body1:
      "Most beginners are stuck watching YouTube videos, copying one-liners, jumping between scripts, and hoping someone hands them an opportunity before they're actually ready.",
    body2:
      'It feels like progress. It isn\'t. It\'s motion without measurement, and it keeps people permanently "almost ready" while better-trained people take the seats.',
    standardTitle: "The 7FCC Standard",
    standard: [
      "Structured training",
      "Daily reps",
      "Live correction",
      "Call review",
      "Clear standards",
      "Real accountability",
    ],
  },
  method: {
    eyebrow: "The System",
    headline: "The 7FCC Method",
    support: "Five disciplined stages that turn raw effort into earned opportunity.",
    stages: [
      {
        num: "1",
        title: "Learn The Framework",
        body: "Master call structure, discovery, and the psychology behind every stage — so you understand why it works, not just what to say.",
      },
      {
        num: "2",
        title: "Practice The Reps",
        body: "Run live drills and roleplays until the framework is automatic under pressure. Reps build the calm that closes.",
      },
      {
        num: "3",
        title: "Track The Activity",
        body: "Log every dial, contact, and booking in the CRM. Your numbers become the map that tells you exactly what to fix.",
      },
      {
        num: "4",
        title: "Review The Calls",
        body: "Use Fireflies to review recordings line by line. Correction at the tape is where skill compounds the fastest.",
      },
      {
        num: "5",
        title: "Earn The Seat",
        body: "Hit the standard, prove the discipline, and become the setter or closer teams actually want to hand leads to.",
      },
    ],
  },
  build: {
    eyebrow: "The Build",
    headline: "What You're Really Building Inside 7FCC",
    support:
      "This isn't a video library you forget about. It's a system that compounds into a real, marketable skill set.",
    items: [
      {
        title: "Setter Certification Path",
        body: "A structured ladder that proves you can book clean, qualified appointments worth a closer's time.",
      },
      {
        title: "Closer Development Path",
        body: "Discovery, money conversations, and decision control — the skills that turn calls into closed revenue.",
      },
      {
        title: "Live Sales Drills",
        body: "Pressure-tested roleplays that build instinct so you stop freezing and start leading the call.",
      },
      {
        title: "Objection Handling Library",
        body: "Field-tested responses to the objections that kill most calls, with the logic behind each one.",
      },
      {
        title: "Fireflies Call Review System",
        body: "A repeatable process to review your recordings and turn every call into targeted correction.",
      },
      {
        title: "CRM Pipeline Discipline",
        body: "The habits that keep your pipeline clean, your follow-up on time, and no opportunity slipping.",
      },
      {
        title: "Follow-Up Frameworks",
        body: 'Sequences that recover "not now" leads and protect the deals most people let die in silence.',
      },
      {
        title: "Performance Scorecards",
        body: "Clear metrics that show exactly where you stand and what to fix this week — no guessing.",
      },
      {
        title: "Community Accountability",
        body: "A room of serious operators who hold the standard with you so motivation never has to.",
      },
      {
        title: "Real Sales Environment Standards",
        body: "Train the way real high-ticket teams operate — so the seat feels familiar before you even sit in it.",
      },
    ],
  },
  tracks: {
    eyebrow: "Choose Your Path",
    headline: "Two Tracks. One Standard.",
    support: "Start where you are. Both paths run on the same discipline.",
    tracks: [
      {
        label: "Track 01",
        title: "Setter Track",
        body: "Become the appointment-setter teams trust with their leads.",
        bullets: [
          "Cold call confidence",
          "Lead follow-up discipline",
          "Appointment-setting frameworks",
          "CRM pipeline management",
          "Fireflies call review",
          "Daily activity standards",
        ],
        cta: { label: "Explore Setter Path", anchor: "assessment" },
      },
      {
        label: "Track 02",
        title: "Closer Track",
        body: "Become the closer who controls the room and the revenue.",
        bullets: [
          "Discovery call structure",
          "Pain and urgency extraction",
          "Money conversations",
          "Objection handling",
          "Closing psychology",
          "Decision control",
        ],
        cta: { label: "Explore Closer Path", anchor: "assessment" },
      },
    ],
  },
  assessment: {
    eyebrow: "Step 1",
    headline: "Sales Readiness Assessment",
    support: "Six questions. An honest read on where you should start.",
    retake: "Retake Assessment",
    resultHeading: "Your Recommended Path",
    applyLabel: "Apply For This Path",
    questions: [
      {
        q: "Which path are you most interested in?",
        key: "path",
        options: [
          { label: "Setter", val: "setter" },
          { label: "Closer", val: "closer" },
          { label: "Both", val: "both" },
        ],
      },
      {
        q: "How many hours per week can you commit?",
        key: "hours",
        options: [
          { label: "Less than 5", val: 0 },
          { label: "5–10", val: 1 },
          { label: "10–20", val: 2 },
          { label: "20+", val: 3 },
        ],
      },
      {
        q: "How comfortable are you making calls?",
        key: "comfort",
        options: [
          { label: "Terrified", val: 0 },
          { label: "Somewhat comfortable", val: 1 },
          { label: "Comfortable", val: 2 },
          { label: "Already making calls", val: 3 },
        ],
      },
      {
        q: "What do you struggle with most?",
        key: "struggle",
        options: [
          { label: "Confidence", val: "confidence" },
          { label: "Objections", val: "objections" },
          { label: "Structure", val: "structure" },
          { label: "Consistency", val: "consistency" },
          { label: "Closing", val: "closing" },
        ],
      },
      {
        q: "Are you willing to record and review calls?",
        key: "review",
        options: [
          { label: "Yes", val: 2 },
          { label: "Not sure", val: 1 },
          { label: "No", val: 0 },
        ],
      },
      {
        q: "Are you willing to be coached directly and corrected?",
        key: "coach",
        options: [
          { label: "Yes", val: 2 },
          { label: "Depends", val: 1 },
          { label: "No", val: 0 },
        ],
      },
    ],
    results: {
      closerDevelopment: {
        title: "Closer Development",
        text: "You're already comfortable on the phone and you want the money conversations. Your focus is discovery structure, urgency, objection handling, and decision control — the skills that turn calls into closed revenue. Start in the Closer track.",
      },
      setterCertification: {
        title: "Setter Certification",
        text: "You've got the discipline and you're comfortable making calls. The fastest seat for you is earning your Setter Certification — booking clean, qualified appointments teams trust — then ramping into closing reps from a position of proof.",
      },
      setterFoundations: {
        title: "Setter Foundations",
        text: "You're early, and that's exactly the right place to build correctly. Setter Foundations installs the call confidence, structure, tracking, and review habits first — so you never have to unlearn bad reps later. Master the base, then earn the climb.",
      },
    },
  },
  calculator: {
    eyebrow: "Run The Numbers",
    headline: "Performance Calculator",
    support:
      "See what disciplined activity actually produces. Adjust the inputs and watch your pipeline build.",
    inputs: [
      { key: "calls", label: "Calls per day", def: 60 },
      { key: "contact", label: "Contact rate (%)", def: 25, max: 100, step: 1 },
      { key: "booking", label: "Booking rate (%)", def: 20, max: 100, step: 1 },
      { key: "show", label: "Show rate (%)", def: 70, max: 100, step: 1 },
      { key: "days", label: "Days worked / month", def: 20, max: 31, step: 1 },
    ],
    insights: {
      volume:
        "<strong>Volume is light.</strong> Before you judge the leads or the offer, put more reps on the board. Right now there isn't enough activity to draw real conclusions — pick up the dials and the data first.",
      booking:
        "<strong>Your booking rate is the leak.</strong> You're getting people on the phone but not into the calendar. Go back to your opener and your objection handling — review the tape and tighten the first 30 seconds.",
      show:
        "<strong>You're booking but losing them before the call.</strong> That's a confirmation and follow-up problem, not a sales problem. Tighten your confirmation sequence and your reminder cadence so the shows climb.",
      strong:
        "<strong>These are real numbers.</strong> Strong activity, clean booking, solid shows — this is what leverage looks like. Protect the standard, keep reviewing the calls, and compound it.",
    },
  },
  objections: {
    eyebrow: "Objection Training",
    headline: 'Handle The "No" Like A Professional',
    support: "A preview of the 7FCC objection library. Pick one and see how we break it down.",
    blockTitles: [
      "What it usually means",
      "The weak response",
      "The 7FCC response",
      "Follow-up to regain control",
    ],
    tabs: [
      {
        label: "“I don't have money”",
        mean: "Usually it's not literal — it means they don't yet see enough value to justify the cost, or they're testing your conviction.",
        weak: '"Okay, no problem. Let me know if anything changes."',
        strong:
          '"Totally fair — most people who get real results here felt the same before they saw what it actually does for them. Can I ask: is it that the money isn\'t there at all, or that you\'re not yet sure this is worth it?"',
        follow:
          '"If the money weren\'t the question, is this something you\'d actually want to move forward on?"',
      },
      {
        label: "“I need to think about it”",
        mean:
          'There\'s an unspoken concern they haven\'t said out loud. "Think about it" is a polite exit, not a real objection.',
        weak: '"Sure, take your time and get back to me whenever."',
        strong:
          '"Makes sense — this should be a real decision, not a rushed one. Usually when someone wants to think, there\'s one specific thing on their mind. What\'s the part you\'d want to be sure about?"',
        follow:
          '"If we solved that one piece right now, would there be anything else holding you back?"',
      },
      {
        label: "“I'm not interested”",
        mean: "Often a reflex before they've understood what you actually do — a wall, not a verdict.",
        weak: '"Okay, sorry to bother you. Have a good day."',
        strong:
          '"Completely fair, and I wouldn\'t expect you to be — we haven\'t talked about anything yet. Quick question so I\'m not wasting your time: are you the person who\'d handle [outcome], or should I be talking to someone else?"',
        follow: '"If I could show you in two minutes why this might be worth a look, would that be fair?"',
      },
      {
        label: "“Can you send me info?”",
        mean:
          'A soft deferral. PDFs don\'t sell — and "send me info" usually means "I want to end this politely."',
        weak: '"Sure! What\'s your email? I\'ll send everything over."',
        strong:
          '"I can — but honestly the info only makes sense once I know what actually matters to you, or you\'ll just get a generic doc. Give me 60 seconds: what\'s the main thing you\'d want it to solve?"',
        follow:
          '"If I send exactly the right piece, are you open to a quick call once you\'ve looked it over?"',
      },
      {
        label: "“I'm too busy”",
        mean: "A priority signal, not a time signal. If it mattered enough, the time would appear.",
        weak: '"No worries, I\'ll try you again some other time."',
        strong:
          '"I hear that — busy is exactly why people use this, because it gives them time back. I\'m not asking for an hour. Are mornings or afternoons usually less chaotic for you?"',
        follow:
          '"If this could take something off your plate instead of adding to it, would 15 minutes be worth it?"',
      },
    ],
  },
  builtFor: {
    eyebrow: "Who It's Built For",
    headline: "Built For The People Who Want The Seat",
    support:
      "No fake testimonials. No invented screenshots. Just an honest picture of who thrives here.",
    quotes: [
      "For students tired of guessing — who want a system instead of another scattered playlist.",
      "For setters who want cleaner appointments that closers actually thank them for.",
      "For closers who want stronger conversations and tighter control of the decision.",
      "For operators who want disciplined salespeople they can hand real leads to without worry.",
      "For people who understand skill comes from reps, not motivation — and are ready to do them.",
    ],
    ctaTitle: "If that sounds like you,",
    ctaBody: "the assessment is the fastest way to find your starting point.",
    cta: { label: "Start The Assessment", anchor: "assessment" },
  },
  qualification: {
    eyebrow: "Qualification",
    headline: "Be Honest About Which Side You're On",
    for: {
      title: "This Is For",
      items: [
        "People willing to make calls",
        "People willing to be coached",
        "People willing to track numbers",
        "People willing to review recordings",
        "People serious about high-ticket sales",
      ],
    },
    notFor: {
      title: "This Is Not For",
      items: [
        "People looking for passive income",
        "People who avoid feedback",
        "People who want scripts without practice",
        "People who blame every lead",
        "People who refuse to execute consistently",
      ],
    },
  },
  standard: {
    eyebrow: "Non-Negotiable",
    headline: "The Standard Is The Separator.",
    support: "Click through today's standard. This is the work — every day, on repeat.",
    items: [
      "Make the calls",
      "Track the numbers",
      "Send the follow-ups",
      "Record the conversations",
      "Review the mistakes",
      "Fix the opener",
      "Handle the objection",
      "Book the appointment",
      "Repeat tomorrow",
    ],
    counterSuffix: "complete",
    closing: "That's the standard. Now do it again tomorrow.",
  },
  final: {
    eyebrow: "Last Step",
    headline: "If You Want The Seat, Train Like You Already Have It.",
    body: "7FCC is for people who are serious about becoming valuable in high-ticket sales through structure, repetition, feedback, and execution.",
    cta1: { label: "Apply For Your Path", href: APPLY_URL },
    cta2: { label: "Take The Sales Readiness Assessment", anchor: "assessment" },
    closing: "Your next level is not more motivation. It is cleaner execution.",
  },
};